<template>
  <div v-rect-loading="loading" class="questionnaire-editor">
    <div class="header">
      <div class="title">{{ pageTitle }}</div>
    </div>
    <WrappedForm
      ref="form"
      class="questionnaire-form"
      labelPosition="left"
      labelWidth="230px"
      :model="questionnaire"
      :formLines="formLines"
    />
    <div class="hide-hm-ratings-control">
      <span class="hide-hm-ratings-label">{{ $t(`${i18nPrefix}.label.hideRatingsFromHm`) }}</span>
      <el-switch
        v-model="questionnaire.hideHmRatings"
        :disabled="isHmRatingsReadonly"
        active-color="#0096cc"
        inactive-color="#909399"
      />
    </div>
    <div class="question-toolbar">
      <div
        v-if="showLockedAlert"
        v-auto-tooltip.always="$t(`${i18nPrefix}.message.lockedQuestionnaire`)"
        class="question-locked-alert"
      >
        {{ $t(`${i18nPrefix}.label.locked`) }}
      </div>
      <el-button v-if="isExistingMode" class="copy-btn" @click="handleCopy">{{ $t(`${i18nPrefix}.btn.copyQuestionnaire`) }}</el-button>
      <el-button v-if="canEditQuestionStructure" class="add-question-btn" @click="openAddQuestionDialog">{{ $t(`${i18nPrefix}.btn.addQuestion`) }}</el-button>
    </div>
    <TableList
      noHead
      noPagination
      dragSortIcon="iconfont iconMore"
      rowKey="questionId"
      :listData="questionRows"
      :tableColumns="questionTableColumns"
      :noAction="isViewMode"
      :dragSortFunc="handleQuestionSort"
    >
      <template #order="{ $index }">{{ $index + 1 }}</template>
      <template #type="{ data }">{{ getTypeLabel(data) }}</template>
      <template #title="{ row }">
        <span class="question-title-link" @click="openQuestionDetailDialog(row.question)">
          {{ getQuestionTitle(row.question) }}
        </span>
      </template>
      <template #required="{ row }">
        <el-switch
          v-model="row.required"
          :disabled="!canEditQuestionItemSettings"
          active-color="#0096cc"
          inactive-color="#909399"
        />
      </template>
      <template #weight="{ row }">
        <el-input
          v-if="isRatingQuestion(row.question.type)"
          v-model="row.weight"
          class="weight-input"
          :disabled="!canEditQuestionItemSettings"
          @input="handleWeightInput(row, $event)"
          @change="autoFillRemainingWeight"
        />
      </template>
      <template #action-btn="{ row }">
        <el-popconfirm
          v-if="canEditQuestionStructure"
          :confirm-button-text="$t('common.btn.confirm')"
          :cancel-button-text="$t('common.btn.cancel')"
          icon="el-icon-info"
          icon-color="red"
          :title="$t('common.message.confirmDelete')"
          @confirm="removeQuestion(row)"
        >
          <template #reference>
            <i class="el-icon-delete delete-icon"></i>
          </template>
        </el-popconfirm>
        <i v-else class="el-icon-delete delete-icon is-disabled"></i>
      </template>
    </TableList>
    <div class="bottom-btn-group">
      <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
      <el-button class="save-btn" :disabled="isSaveDisabled" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
    </div>
    <AddQuestionDialog
      ref="addQuestionDialog"
      :show.sync="showAddQuestionDialog"
      :selectedQuestionIds="selectedQuestionIds"
      :keepStateOnOpen="keepAddQuestionDialogStateOnOpen"
      @confirm="addQuestions"
      @create-question="openCreateQuestionDialog"
    />
    <QuestionEditDialog
      :show="questionDialog.show"
      :isEdit="false"
      @update:show="handleCreateQuestionDialogShowChange"
      @saved="handleQuestionCreated"
    />
    <QuestionEditDialog
      :show.sync="questionDetailDialog.show"
      :question="questionDetailDialog.question"
      isEdit
      readonly
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { HM_FORM_STATUS, HM_QUESTION_TYPE } from '@/constant/questionnaire'
import AddQuestionDialog from '../components/AddQuestionDialog.vue'
import QuestionEditDialog from '../components/QuestionEditDialog.vue'
import { getQuestionTypeLabel, isRatingQuestion } from '../components/questionType'
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'

export default {
  components: {
    WrappedForm,
    TableList,
    AddQuestionDialog,
    QuestionEditDialog
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.questionnaireManagement',
      scoreScales: [],
      questionnaire: this.createEmptyQuestionnaire(),
      questionRows: [],
      showAddQuestionDialog: false,
      shouldRestoreAddQuestionDialog: false,
      keepAddQuestionDialogStateOnOpen: false,
      questionDialog: {
        show: false
      },
      questionDetailDialog: {
        show: false,
        question: null
      },
      nameValidator: {
        checkedName: {
          cn: null,
          en: null
        },
        hasError: false,
        pendingValidation: null,
        syncingRelatedField: false
      }
    }
  },
  computed: {
    questionnaireId () {
      return this.$route.params.questionnaireId
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isViewMode () {
      return this.$route.meta.isViewMode
    },
    isExistingMode () {
      return this.isEditMode || this.isViewMode
    },
    isStructureLocked () {
      return Boolean(this.questionnaire.structureLocked)
    },
    isTopReadonly () {
      return this.isViewMode || this.questionnaire.isInherited || this.isStructureLocked
    },
    isHmRatingsReadonly () {
      return this.isViewMode || this.questionnaire.isInherited
    },
    canEditQuestionStructure () {
      return !this.isViewMode && !this.questionnaire.isInherited && !this.isStructureLocked
    },
    canEditQuestionItemSettings () {
      return !this.isViewMode && !this.questionnaire.isInherited
    },
    isSaveDisabled () {
      return this.isViewMode || this.questionnaire.isInherited
    },
    hasRatingQuestion () {
      return _.some(this.questionRows, item => isRatingQuestion(item.question.type))
    },
    showLockedAlert () {
      return this.isStructureLocked && this.isExistingMode
    },
    pageTitle () {
      const mode = this.isViewMode ? 'view' : this.isEditMode ? 'edit' : 'add'
      return this.$t(`${this.i18nPrefix}.title.questionnaire.${mode}`)
    },
    selectedQuestionIds () {
      return _.map(this.questionRows, 'questionId')
    },
    scoreScaleOptions () {
      return this.scoreScales
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      return {
        nameCn: {
          label: this.$t(`${this.i18nPrefix}.label.questionnaireChineseName`),
          class: 'questionnaire-name-line',
          disabled: this.isTopReadonly,
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }, { validator: this.validateQuestionnaireNameCn, trigger: 'blur' }]
        },
        nameEn: {
          label: this.$t(`${this.i18nPrefix}.label.questionnaireEnglishName`),
          class: 'questionnaire-name-line',
          disabled: this.isTopReadonly,
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }, { validator: this.validateQuestionnaireNameEn, trigger: 'blur' }]
        },
        scoreScaleType: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.scale`),
          class: 'scale-line',
          disabled: this.isTopReadonly,
          option: {
            data: this.scoreScaleOptions,
            labelKey: 'score',
            valueKey: 'scaleType'
          },
          rules: [{ required: this.hasRatingQuestion, message: this.$t(`${this.i18nPrefix}.message.scaleRequiredForRating`), trigger: 'change' }]
        }
      }
    },
    questionTableColumns () {
      return [
        {
          isDragSort: true,
          slotName: 'draggable',
          width: '35px',
          hidden: !this.canEditQuestionStructure
        },
        {
          label: '#',
          align: 'center',
          dataKey: 'order',
          slotName: 'order',
          width: '32px',
          cellClass: 'question-order',
          showOverflowTooltip: false
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.question`),
          dataKey: 'question.titleI18n',
          slotName: 'title',
          width: '364px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.type`),
          dataKey: 'question.type',
          slotName: 'type',
          width: '165px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.required`),
          dataKey: 'required',
          slotName: 'required',
          width: '149px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.scoreWeight`),
          dataKey: 'weight',
          slotName: 'weight',
          width: '152px'
        }
      ]
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('questionnaire', [
      'getHmScoreScaleConfig',
      'getHmQuestionnaireById',
      'copyHmQuestionnaire',
      'createHmQuestionnaire',
      'updateHmQuestionnaire',
      'validateHmQuestionnaireName'
    ]),
    isRatingQuestion,
    getTypeLabel (type) {
      return getQuestionTypeLabel(this, type)
    },
    getQuestionTitle (question) {
      return getI18nPropertyValue(question, 'titleCn', 'titleEn') || _.get(question, 'titleI18n') || ''
    },
    joinQuestionOptions (options, key) {
      return _.chain(options || [])
        .sortBy(['sort_order', 'id'])
        .map(item => _.get(item, key))
        .filter()
        .join('\n')
        .value()
    },
    addQuestionTitleI18n (question) {
      return addI18nProperty(question, 'titleI18n', 'titleCn', 'titleEn')
    },
    async validateQuestionnaireNameEn (rule, value, callback) {
      const nameCn = this.questionnaire.nameCn
      const nameEn = value
      this.validateRelatedNameField('nameCn')
      await this.validateQuestionnaireName(callback, nameCn, nameEn)
    },
    async validateQuestionnaireNameCn (rule, value, callback) {
      const nameCn = value
      const nameEn = this.questionnaire.nameEn
      this.validateRelatedNameField('nameEn')
      await this.validateQuestionnaireName(callback, nameCn, nameEn)
    },
    validateRelatedNameField (field) {
      if (this.nameValidator.syncingRelatedField) {
        return
      }
      this.nameValidator.syncingRelatedField = true
      this.$refs.form.validateField(field, () => {
        this.nameValidator.syncingRelatedField = false
      })
    },
    async validateQuestionnaireName (callback, nameCn, nameEn) {
      const checkedName = this.nameValidator.checkedName
      if (!nameCn && !nameEn) {
        checkedName.cn = null
        checkedName.en = null
        this.nameValidator.hasError = false
        this.nameValidator.pendingValidation = null
      } else if (nameCn !== checkedName.cn || nameEn !== checkedName.en) {
        checkedName.cn = nameCn
        checkedName.en = nameEn
        this.nameValidator.hasError = false
        this.nameValidator.pendingValidation = this.validateHmQuestionnaireName({
          questionnaire_id: this.questionnaireId,
          name_zh: nameCn,
          name_en: nameEn
        }).then(resp => {
          return Boolean(resp?.has_same_name_questionnaire)
        })
      }
      const pendingValidation = this.nameValidator.pendingValidation
      if (pendingValidation) {
        const hasError = await pendingValidation
        if (nameCn !== checkedName.cn || nameEn !== checkedName.en) {
          callback()
          return
        }
        if (pendingValidation === this.nameValidator.pendingValidation) {
          this.nameValidator.hasError = hasError
          this.nameValidator.pendingValidation = null
        }
      }
      if (this.nameValidator.hasError) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.hasDuplicateQuestionnaire`)))
      } else {
        callback()
      }
    },
    createEmptyQuestionnaire () {
      return {
        nameCn: '',
        nameEn: '',
        scoreScaleType: null,
        hideHmRatings: true,
        status: HM_FORM_STATUS.CLOSED,
        structureLocked: false,
        isInherited: false
      }
    },
    async initData () {
      this.loading = true
      try {
        const scoreScaleConfig = await this.getHmScoreScaleConfig()
        this.scoreScales = this.normalizeScoreScales(_.get(scoreScaleConfig, 'scales', {}))
        if (this.isExistingMode) {
          const questionnaire = await this.getHmQuestionnaireById(this.$route.params.questionnaireId)
          const normalizedQuestionnaire = this.normalizeQuestionnaire(questionnaire)
          this.questionnaire = normalizedQuestionnaire
          this.questionRows = _.cloneDeep(normalizedQuestionnaire.questionItems)
        }
        this.refreshQuestionOrder()
      } finally {
        this.loading = false
      }
    },
    getScaleType (score) {
      const value = Number(score)
      return value >= 1 && value <= 10 ? `scale_${value}` : null
    },
    getScoreFromScaleType (scaleType) {
      const score = Number(`${scaleType || ''}`.replace('scale_', ''))
      return score >= 1 && score <= 10 ? score : null
    },
    hasScaleDescription (scale) {
      return _.some(_.get(scale, 'values', []), item => {
        return _.trim(_.get(item, 'description_zh')) || _.trim(_.get(item, 'description_en'))
      })
    },
    normalizeScoreScales (scales) {
      return _.chain(scales)
        .map((scale, scaleType) => ({
          score: Number(_.get(scale, 'max_score')) || this.getScoreFromScaleType(scaleType),
          scaleType: _.get(scale, 'scale_type') || scaleType,
          hasDescription: this.hasScaleDescription(scale)
        }))
        .filter(item => item.score && item.scaleType && item.hasDescription)
        .sortBy('score')
        .value()
    },
    normalizeQuestion (question) {
      const source = question || {}
      const type = source.question_type
      return this.addQuestionTitleI18n({
        id: source.id,
        type,
        titleCn: source.title_zh,
        titleEn: source.title_en,
        optionsCn: this.joinQuestionOptions(source.options, 'label_zh'),
        optionsEn: this.joinQuestionOptions(source.options, 'label_en'),
        status: source.status,
        isLocked: Boolean(source.is_locked),
        isInherited: Boolean(source.is_inherited)
      })
    },
    normalizeQuestionnaireItem (item) {
      const question = this.normalizeQuestion(item.question)
      return {
        id: item.id,
        questionId: item.question_id,
        question,
        required: item.is_required,
        weight: item.score_weight === null || item.score_weight === undefined ? null : Number(item.score_weight),
        order: item.sort_order
      }
    },
    normalizeQuestionnaire (questionnaire) {
      const isInherited = Boolean(questionnaire.is_inherited)
      return {
        id: questionnaire.id,
        nameCn: questionnaire.name_zh,
        nameEn: questionnaire.name_en,
        scoreScaleType: questionnaire.score_scale_type || this.getScaleType(_.get(questionnaire, 'score_scale.max_score')),
        hideHmRatings: questionnaire.hm_privacy_enabled !== false,
        status: questionnaire.status === 'enabled',
        structureLocked: Boolean(questionnaire.is_structure_locked),
        isInherited,
        questionItems: _.map(questionnaire.items || [], item => this.normalizeQuestionnaireItem(item))
      }
    },
    refreshQuestionOrder () {
      this.questionRows = _.map(this.questionRows, (item, index) => ({ ...item, order: index + 1 }))
    },
    addQuestions (questions) {
      const startOrder = this.questionRows.length
      const rows = _.map(questions, (question, index) => {
        return {
          questionId: question.id,
          question: this.addQuestionTitleI18n(_.cloneDeep(question)),
          required: false,
          weight: isRatingQuestion(question.type) ? 0 : null,
          order: startOrder + index + 1
        }
      })
      this.questionRows = _.concat(this.questionRows, rows)
      this.refreshQuestionOrder()
    },
    removeQuestion (row) {
      if (!this.canEditQuestionStructure) {
        return
      }
      this.questionRows = _.filter(this.questionRows, item => item.questionId !== row.questionId)
      this.refreshQuestionOrder()
    },
    handleQuestionSort (newIndex, oldIndex) {
      if (!this.canEditQuestionStructure) {
        return
      }
      const nextRows = _.clone(this.questionRows)
      const target = _.chain(nextRows)
        .thru(rows => _.pullAt(rows, oldIndex))
        .head()
        .value()
      this.questionRows = _.concat(
        _.slice(nextRows, 0, newIndex),
        target ? [target] : [],
        _.slice(nextRows, newIndex)
      )
      this.refreshQuestionOrder()
    },
    openAddQuestionDialog () {
      this.keepAddQuestionDialogStateOnOpen = false
      this.showAddQuestionDialog = true
    },
    openCreateQuestionDialog () {
      this.shouldRestoreAddQuestionDialog = true
      this.keepAddQuestionDialogStateOnOpen = true
      this.showAddQuestionDialog = false
      this.questionDialog.show = true
    },
    handleCreateQuestionDialogShowChange (show) {
      this.questionDialog.show = show
      if (!show && this.shouldRestoreAddQuestionDialog) {
        this.showAddQuestionDialog = true
        this.$nextTick(() => {
          this.shouldRestoreAddQuestionDialog = false
          this.keepAddQuestionDialogStateOnOpen = false
        })
      }
    },
    openQuestionDetailDialog (question) {
      this.questionDetailDialog = {
        show: true,
        question: _.cloneDeep(question)
      }
    },
    handleQuestionCreated () {
      this.shouldRestoreAddQuestionDialog = false
      this.showAddQuestionDialog = true
      this.$nextTick(() => {
        if (this.$refs.addQuestionDialog) {
          this.$refs.addQuestionDialog.fetchQuestions()
        }
      })
    },
    async handleCopy () {
      if (!this.questionnaire.id) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.saveBeforeCopy`))
        return
      }
      this.loading = true
      try {
        const resp = await this.copyHmQuestionnaire(this.questionnaire.id)
        this.$message.success(this.$t('common.message.optionSuccess'))
        this.$router.push({ path: `/v-admin/questionnaire-management/questionnaire/edit/${resp.id}`, query: this.$route.query })
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      this.$router.push({ path: '/v-admin/questionnaire-management/questionnaire/list', query: this.$route.query })
    },
    handleWeightInput (row, value) {
      const inputValue = [null, undefined].includes(value) ? '' : String(value)
      const weight = inputValue.replace(/\D/g, '')
      row.weight = weight === '' ? null : _.clamp(Number(weight), 0, 100)
    },
    autoFillRemainingWeight () {
      const ratingQuestions = _.filter(this.questionRows, item => isRatingQuestion(item.question.type))
      if (ratingQuestions.length <= 1 || _.some(ratingQuestions, item => [null, undefined, ''].includes(item.weight))) {
        return
      }
      const zeroWeightQuestions = _.filter(ratingQuestions, item => Number(item.weight) === 0)
      if (zeroWeightQuestions.length !== 1) {
        return
      }
      const totalWeight = _.sumBy(ratingQuestions, item => Number(item.weight) || 0)
      zeroWeightQuestions[0].weight = _.clamp(100 - totalWeight, 0, 100)
    },
    validateWeight () {
      const ratingQuestions = _.filter(this.questionRows, item => isRatingQuestion(item.question.type))
      if (ratingQuestions.length === 0) {
        return true
      }

      // 0.1 + 0.2 !== 0.3
      const total = _.sumBy(
        ratingQuestions,
        item => Math.round((Number(item.weight) || 0) * 100)
      )

      if (total !== 10000) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.weightTotalInvalid`))
        return false
      }
      if (!this.questionnaire.scoreScaleType) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.scaleRequiredForRating`))
        return false
      }
      if (!_.find(this.scoreScaleOptions, { scaleType: this.questionnaire.scoreScaleType })) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.scaleRequiredForRating`))
        return false
      }
      return true
    },
    validateQuestions () {
      if (this.questionRows.length > 0) {
        return true
      }
      this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionRequired`))
      return false
    },
    buildQuestionnairePayload () {
      const items = _.map(this.questionRows, (item, index) => ({
        id: item.id,
        question_id: item.questionId,
        sort_order: index + 1,
        is_required: item.required,
        score_weight: item.question.type === HM_QUESTION_TYPE.RATING ? Number(item.weight) || 0 : null
      }))
      return {
        name_zh: this.questionnaire.nameCn,
        name_en: this.questionnaire.nameEn,
        score_scale_type: this.questionnaire.scoreScaleType || null,
        hm_privacy_enabled: this.questionnaire.hideHmRatings,
        status: this.questionnaire.status ? 'enabled' : 'disabled',
        items
      }
    },
    async handleSave () {
      const valid = await this.$refs.form.submit()
      if (!valid || !this.validateQuestions() || !this.validateWeight()) {
        return
      }
      this.loading = true
      try {
        if (this.isExistingMode) {
          await this.updateHmQuestionnaire({
            id: this.questionnaire.id,
            payload: this.buildQuestionnairePayload()
          })
        } else {
          const questionnaireInfo = this.buildQuestionnairePayload()
          questionnaireInfo.status = 'enabled'
          await this.createHmQuestionnaire(questionnaireInfo)
        }
        this.$message.success(this.$t('common.message.saveSuccess'))
        this.$router.push({ path: '/v-admin/questionnaire-management/questionnaire/list', query: { ...this.$route.query, page: 1 } })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-editor {
  .header {
    border-bottom: 1px solid #dcdfe6;

    .title {
      padding: 4px 0 18px 10px;
      color: #303133;
      font-size: 20px;
      font-weight: 500;
      line-height: 25px;
    }
  }

  .locked-alert {
    margin: 16px 0;
  }

  .questionnaire-form {
    width: 100%;
    margin-top: 18px;
    margin-left: 10px;

    ::v-deep {
      .el-form-item {
        margin-bottom: 20px;
      }

      .el-form-item__label {
        color: #303133;
        font-size: 14px;
        font-weight: 500;
        line-height: 32px;
        text-align: left;
        white-space: normal;
      }

      .el-input__inner {
        height: 32px;
        line-height: 32px;
      }

      .questionnaire-name-line {
        .el-input {
          padding-right: 60px;
        }
      }

      .scale-line {
        .el-select {
          width: 80px;
        }
      }
    }
  }

  .hide-hm-ratings-control {
    display: flex;
    align-items: center;
    margin: 0 0 18px 10px;

    .hide-hm-ratings-label {
      width: 230px;
      color: #303133;
      font-size: 14px;
      font-weight: 500;
      line-height: 32px;
    }
  }

  .question-toolbar {
    position: relative;
    display: flex;
    justify-content: flex-end;
    margin: 8px 0 16px;

    .question-locked-alert {
      position: absolute;
      left: 10px;
      top: 6px;
      border: 1px solid #d95f63;
      padding: 4px 8px;
      border-radius: 2px;
      font-size: 14px;
      font-weight: 500;
      color: #d95f63;
      line-height: 14px;
      background: #d95f631a;
    }

    .copy-btn,
    .add-question-btn {
      border-color: #0096cc;
      color: #0096cc;
      padding: 5px 12px;
      line-height: 20px;
      white-space: normal;
    }
  }

  .list-page {
    ::v-deep {
      .list-container {
        .question-order {
          .cell {
            padding: 0 4px;
          }
        }
      }
    }
  }

  .required-text {
    color: #0096cc;
  }

  .question-title-link {
    color: #0096cc;
    cursor: pointer;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .weight-input {
    width: 110px;
  }

  .delete-icon {
    color: #0096cc;
    cursor: pointer;

    &.is-disabled {
      color: #c0c4cc;
      cursor: not-allowed;
    }
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;

    .cancel-btn {
      padding: 12px 15px;
      white-space: normal;
    }

    .save-btn {
      margin-left: 12px;
      padding: 12px 36px;
      color: #ffffff;
      background: #0096cc;
      border-color: #0096cc;
      white-space: normal;
    }
  }
}
</style>
